import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin, DatePicker } from 'antd';
import { errHandler } from '../../helper/helper';
import { getLoanSummary } from "./LoanService";
import { loanSummaryExcel } from '../../Utils/Loan/LoanSummaryExcel';
import { headerStyle, subtotalStyle } from "../../Utils/excelStyle";

const { Option } = Select;

const LoanSummary = ({
    loginPlaceId,
    products,
    districtData,
    districtName,
    branchId,
    divisionalAreaId,
}) => {
    const [selections, setSelections] = useState({
        districts: [],
        divisionalAreas: [],
        branches: [],
        products: [],
        date: null
    });

    const [availableBranches, setAvailableBranches] = useState([]);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [loading, setLoading] = useState(false);

    // Update divisional areas
    useEffect(() => {
        let filteredDivisionalAreas = [];
        console.log("Divisional Area ID:", divisionalAreaId);

        if (loginPlaceId === "2" && divisionalAreaId) {
            filteredDivisionalAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === divisionalAreaId);

            setSelections(prev => ({ ...prev, divisionalAreas: [divisionalAreaId] }));
        } else {
            filteredDivisionalAreas = districtData
                .filter(d => selections.districts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);

            setSelections(prev => ({
                ...prev,
                divisionalAreas: prev.divisionalAreas.filter(did =>
                    filteredDivisionalAreas.some(d => d.divAId === did)
                )
            }));
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [selections.districts, districtData, loginPlaceId, divisionalAreaId]);

    // Update branches based on districts
    useEffect(() => {
        const filtered = districtData
            .filter(d => selections.districts.includes(d.districtId))
            .flatMap(d => d.branchDtoList || []);

        setAvailableBranches(filtered);
        setSelections(prev => ({
            ...prev,
            branches: prev.branches.filter(bid => filtered.some(b => b.id === bid))
        }));
    }, [selections.districts, districtData]);

    // Update branches based on divisional areas
    useEffect(() => {
        const filteredBranches = districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => selections.divisionalAreas.includes(div.divAId))
            .flatMap(div => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);
        setSelections(prev => ({
            ...prev,
            branches: prev.branches.filter(bid => filteredBranches.some(b => b.id === bid))
        }));
    }, [selections.divisionalAreas, districtData]);

    const getSelectedDistrictNames = () =>
        districtData
            .filter(d => selections.districts.includes(d.districtId))
            .map(d => d.districtName)
            .join(", ");

    const getSelectedDivisionalAreaNames = () =>
        districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => selections.divisionalAreas.includes(div.divAId))
            .map(div => div.divAIdName)
            .join(", ");

    const title =
        loginPlaceId === "2"
            ? getSelectedDivisionalAreaNames()
            : loginPlaceId === "1"
                ? ""
                : getSelectedDistrictNames();
    const header = `${title} Loan Summary Report-${selections.date ? selections.date.format("YYYY-MM-DD") : null}`;

    // Generate report
    const generateReport = async () => {
        const data = {
            branchIdList: loginPlaceId === "1" ? [branchId] : selections.branches,
            productIdList: selections.products,
            selectedDate: selections.date ? selections.date.format("YYYY-MM-DD") : null
        };

        setLoading(true);
        getLoanSummary(data)
            .then(res => {
                loanSummaryExcel(res.data, headerStyle, subtotalStyle, header, loginPlaceId);
            })
            .catch(err => errHandler(err))
            .finally(() => setLoading(false));
    };

    const isGenerateDisabled = 
        loading || 
        (loginPlaceId !== "1" && selections.branches.length === 0) || 
        selections.products.length === 0;

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    {/* District Selection */}
                    {(loginPlaceId === "4" || loginPlaceId === "3") && (
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selections.districts}
                                        onChange={(values) => {
                                            if (values.includes("ALL")) {
                                                setSelections(prev => ({ ...prev, districts: districtData.map(d => d.districtId) }));
                                            } else {
                                                setSelections(prev => ({ ...prev, districts: values }));
                                            }
                                        }}
                                        maxTagCount="responsive"
                                    >
                                        <Option key="ALL" value="ALL">🌍 Select All Districts</Option>
                                        {districtData
                                            .filter(d => loginPlaceId === "4" ? d.districtName === districtName : true)
                                            .map(d => (
                                                <Option key={d.districtId} value={d.districtId}>
                                                    🏙️ {d.districtName}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    )}

                    {/* Divisional Areas */}
                    {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (
                        <Col span={24}>
                            <Form.Item label="Select Divisional Areas">
                                <Select
                                    mode="multiple"
                                    placeholder="Select divisional areas"
                                    style={{ width: '100%' }}
                                    value={selections.divisionalAreas}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            setSelections(prev => ({ ...prev, divisionalAreas: availableDivisionalAreas.map(d => d.divAId) }));
                                        } else {
                                            setSelections(prev => ({ ...prev, divisionalAreas: values }));
                                        }
                                    }}
                                    maxTagCount="responsive"
                                >
                                    <Option key="ALL" value="ALL">🌐 Select All Divisional Areas</Option>
                                    {availableDivisionalAreas.map(d => (
                                        <Option key={d.divAId} value={d.divAId}>
                                            🏙️ {d.divAIdName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    {/* Branches */}
                    {(loginPlaceId === "2" || loginPlaceId === "3" || loginPlaceId === "4") && (
                        <Col span={24}>
                            <Form.Item label="Select Branch(es)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: '100%' }}
                                    value={selections.branches}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            setSelections(prev => ({ ...prev, branches: availableBranches.map(b => b.id) }));
                                        } else {
                                            setSelections(prev => ({ ...prev, branches: values }));
                                        }
                                    }}
                                    disabled={availableBranches.length === 0}
                                    maxTagCount="responsive"
                                >
                                    <Option key="ALL" value="ALL">🏢 Select All Branches</Option>
                                    {availableBranches.map(branch => (
                                        <Option key={branch.id} value={branch.id}>
                                            🏢 {branch.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    )}

                    {/* Products */}
                    <Col span={24}>
                        <Form.Item label="Select Products">
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="Select products"
                                style={{ width: '100%' }}
                                value={selections.products}
                                onChange={(values) => {
                                    if (values.includes('ALL')) {
                                        setSelections(prev => ({ ...prev, products: products.map(b => b.productId) }));
                                    } else {
                                        setSelections(prev => ({ ...prev, products: values }));
                                    }
                                }}
                                maxTagCount="responsive"
                            >
                                <Option key="ALL" value="ALL">🔘 Select All Products</Option>
                                {products.map(product => (
                                    <Option key={product.productId} value={product.productId}>
                                        {product.productName}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>

                    {/* Date */}
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item label="Select Date">
                                <DatePicker
                                    onChange={(date) => setSelections(prev => ({ ...prev, date }))}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* Generate Report Button */}
                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={generateReport}
                                    disabled={isGenerateDisabled}
                                    className="generate-btn"
                                >
                                    {loading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default LoanSummary;
